import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { Import } from "@/types/Import";
import { Show } from "@/components/core";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { Button } from "@/components/ui/button";
import { useImportDetailsController } from "./useImportDetailsController";
import { GridContainer } from "@/components/grid";
import { ImportStatusIndicator } from "../components/ImportStatusIndicator";
import { RowSelectedEvent } from "ag-grid-community";
import { SourceViewer } from "./SourceViewer";
import { ImportRow } from "@/types";
import { CheckCircle, Filter, Eye, Table, FileText, ArrowRight, Clock } from "lucide-react";

interface ImportDetailsProps {
  selectedImport?: Import;
}

export const ImportDetails = (props: ImportDetailsProps) => {
  const controller = useImportDetailsController(props.selectedImport);
  const [showTable, setShowTable] = useState(false);

  const hasErrors = controller.currentImport?.errors && (
    (typeof controller.currentImport.errors === "string" && (controller.currentImport.errors as string).trim() !== "") ||
    (typeof controller.currentImport.errors === "number" && controller.currentImport.errors > 0)
  );
  
  const noRowsImported = controller.currentImport?.status === "Finished" && 
    (!controller.importedRows || controller.importedRows.length === 0) &&
    (!controller.currentImport?.rowCount || controller.currentImport.rowCount === 0);
  
  const isImportSuccessful = controller.currentImport?.status === "Finished" && !hasErrors && !noRowsImported;
  
  const isImportStarted = controller.currentImport?.status === "Started";

  return (
    <div className="flex-1 p-4 overflow-y-auto">
      <Show if={controller.currentImport === undefined}>
        <div className="flex items-center justify-center h-full">
          <Card className="w-full max-w-lg border-dashed border-2 border-muted-foreground/30 bg-muted/5">
            <CardContent className="pt-8 pb-8">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="h-20 w-20 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full flex items-center justify-center border-2 border-blue-200/50">
                  <FileText className="h-10 w-10 text-blue-600" />
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-foreground">
                    Import Details
                  </h3>
                  <p className="text-muted-foreground max-w-sm">
                    Choose an import from the list to view its details, status, and data
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/20 px-4 py-2 rounded-full">
                  <span>Select an import</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Show>
      <Show if={controller.currentImport !== undefined}>
        <div className="cd-container flex flex-col mb-2 h-full gap-2">
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Import Details</CardTitle>
                <CardDescription>
                  View details for import {controller.currentImport?.importId}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Import ID
                    </h3>
                    <p>{controller.currentImport?.importId}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Date
                    </h3>
                    <p>
                      {controller.currentImport?.createdAt
                        ? new Date(
                            controller.currentImport.createdAt
                          ).toLocaleDateString()
                        : ""}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Context
                    </h3>
                    <p>{controller.currentImport?.context}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      File Name
                    </h3>
                    <p>{controller.currentImport?.fileName}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Rows
                    </h3>
                    <p>
                      {(controller.importedRows?.length || 0) > 0 
                        ? controller.importedRows?.length 
                        : controller.currentImport?.rowCount || 0}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Status
                    </h3>
                    <ImportStatusIndicator
                      importItem={controller.currentImport as Import}
                    />
                  </div>
                </div>
                
                {hasErrors && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Import Errors</h4>
                    <p className="text-sm text-red-700">
                      {typeof controller.currentImport?.errors === "string" 
                        ? controller.currentImport.errors 
                        : `${controller.currentImport?.errors} errors occurred`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <Show if={isImportStarted}>
            <div className="flex flex-1 items-center justify-center">
              <Card className="w-full max-w-md">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="h-8 w-8 text-blue-600 animate-pulse" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-blue-800">
                        Import In Progress
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        The import is currently being processed. Please wait for it to complete.
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground bg-blue-50 px-3 py-2 rounded-md">
                      Import ID: {controller.currentImport?.importId}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                      <span className="text-sm text-blue-700">Processing...</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </Show>
          
          <Show if={isImportSuccessful && !showTable}>
            <div className="flex flex-1 items-center justify-center">
              <Card className="w-full max-w-md">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-green-800">
                        Import Completed Successfully
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        All {controller.importedRows?.length} rows have been processed without errors.
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground bg-green-50 px-3 py-2 rounded-md">
                      Import ID: {controller.currentImport?.importId}
                    </div>
                    <Button
                      onClick={() => setShowTable(true)}
                      className="flex items-center gap-2 mt-4"
                      variant="outline"
                    >
                      <Table className="h-4 w-4" />
                      View Import Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </Show>

          <Show if={noRowsImported}>
            <div className="flex flex-1 items-center justify-center">
              <Card className="w-full max-w-md">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
                      <FileText className="h-8 w-8 text-red-600" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-red-800">
                        No Rows Imported
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        The import completed but no rows were processed. Please check your file format and try again.
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground bg-red-50 px-3 py-2 rounded-md">
                      Import ID: {controller.currentImport?.importId}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </Show>

          <Show if={!isImportSuccessful && !noRowsImported && !isImportStarted || showTable}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium">
                    {controller.showAllRows 
                      ? `All Rows (${controller.totalRowCount})` 
                      : `Invalid Rows (${controller.invalidRowCount} of ${controller.totalRowCount})`
                    }
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {isImportSuccessful && showTable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTable(false)}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Back to Success
                    </Button>
                  )}
                  <Button
                    variant={controller.showAllRows ? "outline" : "default"}
                    size="sm"
                    onClick={() => controller.setShowAllRows(!controller.showAllRows)}
                    className="flex items-center gap-2"
                  >
                    {controller.showAllRows ? (
                      <>
                        <Filter className="h-4 w-4" />
                        Show Invalid Only
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Show All Rows
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex flex-1">
                <div className="flex flex-1 aaaaa">
                  <GridContainer>
                    <AgGridReact
                      key={`import-${props.selectedImport?.importId}-${controller.showAllRows}`}
                      rowData={controller.filteredRows}
                      headerHeight={48}
                      rowHeight={40}
                      rowSelection="single"
                      suppressHorizontalScroll={false}
                      alwaysShowHorizontalScroll={false}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        resizable: true,
                      }}
                      columnDefs={controller.columnDefs}
                      onGridReady={(e) => {
                        controller.setGridApi(e.api);
                      }}
                      onRowSelected={(e: RowSelectedEvent) => {
                        if (e.node.data && e.node.data.id) {
                          controller.setSelectedRow(e.node.data as ImportRow);
                        } else {
                          controller.setSelectedRow(undefined);
                        }
                      }}
                    />
                  </GridContainer>
                </div>
                <div className="w-[500px] border-y border-r border-gray-400">
                  <SourceViewer
                    currentImport={controller.currentImport}
                    row={controller.selectedRow}
                  />
                </div>
              </div>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};
